
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { RoleValidation } from '@/modules/GatewayModule/validations/Role.validation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { GatewayPermissionT, GatewayRoleT } from '@/core/types/gateway.types'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'
import { CountryT } from '@/core/types/companies.types'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<GatewayRoleT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel', 'delete'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            name: null as string | null,
            description: null as string | null,
            permissions: [] as number[],
        })

        const state_form$ = useVuelidate(RoleValidation, state)

        const data_state = reactive({
            permissions: new DataState<GatewayPermissionT>({}),
        })

        const loadPermissions = async (page: number, search?: string) => {
            data_state.permissions.pagination.current_page = page

            try {
                data_state.permissions.is_loading = true

                const response = await gateway.getPermissions(page, search)
                data_state.permissions.list = response.data
                data_state.permissions.pagination.total_pages = response.meta.last_page

                data_state.permissions.is_failed = false
                data_state.permissions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.permissions.failure.catchError(error)

                if (!data_state.permissions.failure.error_state.is_cancel) {
                    data_state.permissions.is_failed = true
                }
            } finally {
                if (!data_state.permissions.failure.error_state.is_cancel) {
                    data_state.permissions.is_loading = false
                }
            }
        }

        const loadMorePermissions = async (page: number) => {
            if (page > data_state.permissions.pagination.total_pages) return
            data_state.permissions.pagination.current_page = page

            try {
                data_state.permissions.is_loading = true

                const response = await gateway.getPermissions(page)
                data_state.permissions.list = data_state.permissions.list.concat(response.data)
                data_state.permissions.pagination.total_pages = response.meta.last_page

                data_state.permissions.is_failed = false
                data_state.permissions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.permissions.failure.catchError(error)

                if (!data_state.permissions.failure.error_state.is_cancel) {
                    data_state.permissions.is_failed = true
                }
            } finally {
                if (!data_state.permissions.failure.error_state.is_cancel) {
                    data_state.permissions.is_loading = false
                }
            }
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление роли'
            if (props.isUpdate) return 'Редактирование роли'
            if (props.isDelete) return 'Удаление роли'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const payload = {
                name: state.name,
                description: state.description,
                permissions: state.permissions,
            }

            emit('create', payload)
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', toRaw(state))
        }

        const emitDelete = () => {
            emit('delete', state.id)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const searchPermissions = async (search_query: string) => {
            await loadPermissions(1, search_query)
        }

        const unshiftFoundPermission = async (permission: GatewayPermissionT) => {
            await loadPermissions(data_state.permissions.pagination.current_page)
            data_state.permissions.list.unshift(permission)
        }

        onBeforeMount(() => {
            loadPermissions(1)
            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.name = props.initialValue.name
            state.description = props.initialValue.description
            state.permissions = props.initialValue.permissions.map((permission) => permission.id)
        })

        return {
            state,
            state_form$,
            data_state,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            loadPermissions,
            loadMorePermissions,
            searchPermissions,
            unshiftFoundPermission,
        }
    },
})
